import React, { useState, useEffect, useCallback } from 'react'
import { isMobile, isMobileOnly } from 'mobile-device-detect'
import { View, Views } from 'react-big-calendar'

import moment from 'moment'

import { CalendarEventEditModal } from 'components/Calendar/Meetings/CalendarEventEditModal'
import { CalanderWrapper } from 'components/Calendar/style'
import { getReturnToPath, getRoleFromPath, navigateTo } from 'utils/helper'
import { CalenderSettingContainer } from 'pages/Owner/container'
import { isEmpty } from 'lodash'
import { CalendarViewer } from 'components/Calendar/Meetings/CalendarViewer'
import { useLocation } from 'react-router-dom'
import useGetStartEndDate from 'hooks/useGetStartEndDate'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'
import { CALENDAR } from 'config'
import {
  GetUserCalendarEventsQueryVariables,
  UserProfile,
} from '../../../__generated__/api-types-and-hooks'
import { ReduxAction } from 'store/types'
import { ICalendarEvent } from 'types'
import history from 'utils/history'

moment.updateLocale('ko', {
  week: {
    dow: 0,
    doy: 1,
  },
})

const checkMobile = () => {
  return window.innerWidth < 500 || isMobileOnly
}

interface IMeetingsProps {
  user: UserProfile
  currentCalendarDate: Date
  errorMessage: string
  refetch: boolean
  isDisabled: boolean
  isMeetingDeletion: boolean
  cancelMeetingLoading: boolean
  isMeetingModalOpen: boolean
  showInternalMeetingsOnly: boolean
  calenderEventsLoading: boolean
  events: ICalendarEvent[]
  stepType
  userMeeting
  advisorMeetingRequestButtonEnabled?: boolean
  setCurrentCalendarDateAction: ReduxAction<Date>
  getUserCalendarEventsAction: ReduxAction<GetUserCalendarEventsQueryVariables>
  getMyToolsAction: ReduxAction<void>
  totalUpComingMeetingsAction
  cancelUserCalendarEventAction
  resetUserCalendarEventsAction
  updateUserCalendarEventAction
  getUserMeetingByEventIdAction
  openMeetingConfirmationModalBoxAction
}

const Meetings = ({
  user,
  events,
  refetch,
  stepType,
  isDisabled,
  errorMessage,
  userMeeting,
  isMeetingDeletion,
  cancelMeetingLoading,
  isMeetingModalOpen,
  advisorMeetingRequestButtonEnabled,
  cancelUserCalendarEventAction,
  getUserCalendarEventsAction,
  totalUpComingMeetingsAction,
  resetUserCalendarEventsAction,
  updateUserCalendarEventAction,
  getMyToolsAction,
  showInternalMeetingsOnly,
  getUserMeetingByEventIdAction,
  calenderEventsLoading,
  openMeetingConfirmationModalBoxAction,
  currentCalendarDate,
  setCurrentCalendarDateAction,
}: IMeetingsProps) => {
  const [view, setView] = useState<View>(checkMobile() ? Views.DAY : Views.MONTH)
  const [calendarEvents, setCalendarEvents] = useState<ICalendarEvent[]>([])
  const [openModal, setOpenModal] = useState(false)
  const [selectedEvent, setSelectedEvent]: any = useState()
  const modalHandler = () => {
    setOpenModal(!openModal)
    // Reset to read-only mode when modal closes
    if (openModal) {
      setShowEditButton(true)
    }
  }
  const [timeMin, setTimeMin] = useState(moment().toISOString())
  const [timeMax, setTimeMax] = useState(moment().toISOString())
  const [showEditButton, setShowEditButton] = useState(true)
  const location = useLocation()

  const userRole = getRoleFromPath()
  useEffect(() => {
    totalUpComingMeetingsAction(5)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { start_date, end_date } = useGetStartEndDate(currentCalendarDate)

  useEffect(() => {
    setTimeMin(start_date)
    setTimeMax(end_date)

    if (refetch || !isMeetingModalOpen) {
      let payload = {
        calendarName: showInternalMeetingsOnly ? CALENDAR.INTERNAL : '',
        timeMin: moment(start_date).format('YYYY-MM-DDTHH:mm:ss-00:00'),
        timeMax: moment(end_date).format('YYYY-MM-DDTHH:mm:ss-00:00'),
      }
      getUserCalendarEventsAction(payload)
      getMyToolsAction()
    }

    return () => resetUserCalendarEventsAction()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentCalendarDate, refetch, isMeetingModalOpen, showInternalMeetingsOnly])

  useEffect(() => {
    setCalendarEvents(events)
  }, [events])

  useEffect(() => {
    const param = getReturnToPath()
    if (param.eventId && !isEmpty(calendarEvents)) {
      const event = calendarEvents.find((event) => event.eventId === param.eventId)
      setSelectedEvent(event)
      setOpenModal(true)
      history.replace(`/${userRole}/${user.tenantId}/calendar`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarEvents])

  useEffect(() => {
    const splitPath = window.location.href.split('details=')
    const eventId = splitPath[splitPath.length - 1]
    const shouldFetchSpecificMeeting =
      eventId &&
      (isEmpty(userMeeting?.id) || userMeeting?.id !== eventId) &&
      window.location.href.includes('details')

    if (shouldFetchSpecificMeeting) {
      getUserMeetingByEventIdAction({ id: eventId })
    }

    const shouldShowMeetingDetailsModal =
      !isEmpty(userMeeting?.id) &&
      window.location.href.includes('details') &&
      userMeeting?.id === eventId

    if (shouldShowMeetingDetailsModal) {
      setSelectedEvent(userMeeting)
      setOpenModal(true)
      navigateTo(userRole, 'calendar')
    }
    // eslint-disable-next-line
  }, [userMeeting?.title, location.key])

  const onSelectEvent = useCallback(
    (calEvent) => {
      setOpenModal(!openModal)
      setSelectedEvent(calEvent)
    },
    [openModal]
  )
  return (
    <>
      {openModal === true ? (
        <CalendarEventEditModal
          user={user}
          closeModal={modalHandler}
          cancelUserCalendarEventAction={cancelUserCalendarEventAction}
          timeMin={timeMin}
          timeMax={timeMax}
          event={selectedEvent}
          showEditButton={showEditButton}
          setShowEditButton={setShowEditButton}
          updateMeetingAction={updateUserCalendarEventAction}
          openMeetingConfirmationModalBoxAction={openMeetingConfirmationModalBoxAction}
          stepType={stepType}
          isMeetingDeletion={isMeetingDeletion}
          errorMessage={errorMessage}
          isDisabled={isDisabled || cancelMeetingLoading}
        />
      ) : null}

      {/* Calendar */}
      <div
        id="calendar"
        className="xs:col-span-12 lg:col-span-10 mt-8 lg:block"
        data-testid="calendar-panel"
      >
        {calenderEventsLoading ? (
          <div className="h-full flex flex-col items-center p-10">
            <GradientCircleNotchIcon width="100" height="100" />
            <p className="font-bold mt-2">Loading Calendar</p>
          </div>
        ) : (
          <CalanderWrapper>
            <CalendarViewer
              calendarEvents={calendarEvents}
              view={view}
              date={currentCalendarDate}
              onSelectEvent={onSelectEvent}
              isMobile={isMobile}
              setView={setView}
              setDate={setCurrentCalendarDateAction}
            />
          </CalanderWrapper>
        )}
      </div>

      {/* calendar details & settings */}
      <CalenderSettingContainer
        advisorMeetingRequestButtonEnabled={advisorMeetingRequestButtonEnabled}
      />
    </>
  )
}

export default Meetings
